import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { API, AuthContext } from '@/App';
import { Upload, Download } from 'lucide-react';

export default function ImportVendorsDialog({ onClose, onDone }) {
  const { token } = useContext(AuthContext);
  const [csv, setCsv] = useState('');
  const [xlsxB64, setXlsxB64] = useState('');
  const [xlsxName, setXlsxName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const payload = () => (xlsxB64 ? { xlsx_base64: xlsxB64 } : { csv });

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(null);
    if (/\.xlsx$/i.test(f.name)) {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      // Strip the data: prefix — the ingress WAF blocks base64 data URLs.
      setXlsxB64(String(dataUrl).substring(String(dataUrl).indexOf(',') + 1));
      setXlsxName(f.name); setCsv('');
    } else {
      setCsv(await f.text()); setXlsxB64(''); setXlsxName('');
    }
  }
  async function doPreview() {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/import/preview`, payload(), headers);
      setPreview(r.data);
    } catch { toast.error('Could not parse file'); } finally { setBusy(false); }
  }
  async function doConfirm() {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/import/confirm`, payload(), headers);
      toast.success(`Imported ${r.data.created} vendor(s) as Registered / Pending`);
      onDone?.(); onClose();
    } catch { toast.error('Import failed'); } finally { setBusy(false); }
  }
  async function template() {
    try {
      const r = await axios.get(`${API}/service-providers/import/template`, { ...headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'provider_import_template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download template'); }
  }
  const canPreview = !!(csv.trim() || xlsxB64);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="import-vendors-dialog">
        <DialogHeader><DialogTitle className="text-[#0E2A47]">Import Vendors (CSV or XLSX)</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Imported vendors are created as <strong>Registered / Pending</strong>. Import never qualifies a vendor or assigns a QSP number, never overwrites existing companies, and excludes employment applications.</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={template} data-testid="import-template-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"><Download size={14} />Download template</button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
            <Upload size={14} />Choose CSV / XLSX file
            <input type="file" accept=".csv,.xlsx,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          </label>
          {xlsxName && <span className="self-center text-xs font-medium text-emerald-700" data-testid="import-xlsx-name">{xlsxName}</span>}
        </div>
        {!xlsxB64 && (
          <textarea value={csv} onChange={(e) => { setCsv(e.target.value); setPreview(null); }} data-testid="import-csv-text"
            placeholder="Or paste CSV here (first row = column headers)…" className="h-32 w-full rounded-md border border-slate-200 p-2 font-mono text-xs" />
        )}
        {!preview ? (
          <button onClick={doPreview} disabled={busy || !canPreview} data-testid="import-preview-btn"
            className="self-start rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Preview</button>
        ) : (
          <div data-testid="import-preview-result">
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="rounded bg-emerald-100 px-2 py-1 font-semibold text-emerald-800">{preview.to_import} will import</span>
              <span className="rounded bg-amber-100 px-2 py-1 font-semibold text-amber-800">{preview.incomplete} incomplete (flagged)</span>
              <span className="rounded bg-slate-100 px-2 py-1 font-semibold text-slate-700">{preview.duplicates} duplicates skipped</span>
              <span className="rounded bg-rose-100 px-2 py-1 font-semibold text-rose-700">{preview.excluded} employment apps excluded</span>
              {preview.ambiguous_divisions > 0 && <span className="rounded bg-orange-100 px-2 py-1 font-semibold text-orange-800">{preview.ambiguous_divisions} need division review</span>}
            </div>
            <div className="mt-2 max-h-48 overflow-auto rounded border border-slate-100 text-xs">
              {preview.rows.map((r) => (
                <div key={r.row} className="flex items-center justify-between border-b border-slate-50 px-2 py-1">
                  <span>{r.company_name || <em className="text-rose-500">no name</em>}</span>
                  <span className={r.will_import ? 'text-emerald-700' : 'text-slate-400'}>
                    {r.excluded_employment_application ? 'excluded (employment)' : r.duplicate_existing ? 'duplicate (exists)' : r.duplicate_in_file ? 'duplicate (in file)' : r.division_review_needed ? 'import · review division' : r.incomplete ? 'import · incomplete' : 'import'}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={doConfirm} disabled={busy || preview.to_import === 0} data-testid="import-confirm-btn"
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm import ({preview.to_import})</button>
              <button onClick={() => setPreview(null)} className="rounded-md border border-slate-200 px-4 py-2 text-sm">Back</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
